<template>
  <onboarding-form-layout
    :title="$t('incremental_onboarding.ownership.title')"
    :description="$t('incremental_onboarding.ownership.description')"
    :loading="false"
    :show-previous="!isUpdate"
    @proceed="proceed"
    @previous="() => $emit('previous')"
  >
    <ErrorCodeNotification v-if="errorCodes && errorCodes.length > 0" :error-codes="errorCodes" />
    <div class="control-person beneficiary-section s-flex s-flex-col s-items-start s-gap-2">
      <div class="sub-header s-text-lg s-font-semibold">
        {{ $t('incremental_onboarding.ownership.controlPerson.sectionTitle') }}
        <span class="s-text-sm s-text-red-500">*</span>
      </div>
      <div class="subtitle s-text-sm s-text-neutral-500 s-leading-snug s-text-left">
        {{ $t('incremental_onboarding.ownership.controlPerson.sectionDescription') }}
      </div>
      <BeneficiariesList 
        v-if="isControlPersonValid"
        :edit-action="editBeneficiary" :show-control-person="true"
        :delete-action="deleteBeneficiary"
      />
      <s-button
        @click="openBeneficiaryPopup" v-if="!isControlPersonValid" 
        variant="neutral"
        size="large"
        class="s-mt-2"
      >
        {{ $t('incremental_onboarding.ownership.controlPerson.addButton') }}
      </s-button>
      <s-inline-error v-if="Boolean(ownershipError)" :message="ownershipError" />
      <s-inline-error v-if="missingControlPersonError" :message="$t('incremental_onboarding.ownership.controlPerson.missing')" />
    </div>

    <div
      class="additional-beneficiaries beneficiary-section s-flex s-flex-col s-items-start s-gap-2"
      v-if="canAddAdditionalBeneficiary"
    >
      <div class="sub-header s-text-lg s-font-semibold">
        {{ $t('incremental_onboarding.ownership.beneficialOwners.sectionTitle') }}
      </div>
      <div class="subtitle s-text-sm s-text-neutral-500 s-leading-snug s-text-left">
        {{ $t('incremental_onboarding.ownership.beneficialOwners.sectionDescription') }}
      </div>
      <BeneficiariesList
        v-if="getAdditionalBeneficiaries.length > 0" :edit-action="editBeneficiary"
        :show-additional-owners="true" :delete-action="deleteBeneficiary" :read-only="readOnly"
      />
      <s-dropdown :options="BeneficialOwnerTypes()" @select="createBeneficiaryWithOwnerType" class="s-mt-2">
        <s-button
          slot="trigger" :disabled="(!canAddMoreBeneficiaries) || readOnly" 
          variant="neutral"
          size="large"
        >
          {{ $t('incremental_onboarding.ownership.beneficialOwners.addButton') }}
        </s-button>
      </s-dropdown>

      <div class="subtitle s-text-sm" v-if="!canAddMoreBeneficiaries">
        {{ $t('addBeneficiary.exceededMaxNumberOfBeneficiaries') }}
      </div>
    </div>

    <AddBeneficiaryModal
      :open-model="openModel" @close="() => openModel = false" :i="activeBeneficiaryIndex"
      :is-control-person="activeBeneficiaryIndex === 0" :edit-mode="editMode"
      :on-dismiss="onDismissFromAddBeneficiary"
      :read-only="readOnly"
      :is-update="isUpdate"
    />
  </onboarding-form-layout>
</template>

<script>
import merchantOnboardingApi from "@/api/merchantOnboardingApi";
import { OnboardingRequest } from "@/api/paymentApi";
import OnboardingFormLayout from "@/components/incremental-onboarding/OnboardingFormLayout.vue";
import AddBeneficiaryModal from "@/components/onboarding/AddBeneficiaryModal.vue";
import BeneficiariesList from "@/components/onboarding/BeneficiariesList";
import BeneficialOwnerTypes from "@/data/beneficial-owner-types";
import { StakeholderField } from '@/store/modules/onboarding/stakeholderDetails';
import { mapActions, mapGetters, mapState } from "vuex";
import ErrorCodeNotification from "@/components/incremental-onboarding/ErrorCodeNotification.vue";
import FullStoryHelper from '@/app/utils/fullstory';
import {OWNERSHIP_OVERVIEW_PROCEED, UPDATE_OWNERSHIP_OVERVIEW_PROCEED} from '@/app/utils/fullstory/constants';

export default {
  name: 'OwnershipOverview',
  components: { ErrorCodeNotification, AddBeneficiaryModal, BeneficiariesList, OnboardingFormLayout},
  props: {
    readOnly: {
      type: Boolean,
      required: false,
      default: false,
    },
    isUpdate: {
      type: Boolean,
      required: false,
      default: false,
    },
    errorCodes: {
      type: Array,
      required: false,
      default: () => [],
    },
  },
  data: () => {
    return {
      activeBeneficiaryIndex: 0,
      openModel: false,
      editMode: false,
      beneficiaryBackup: null,
      missingControlPersonError: false,
    }
  },
  computed: {
    ...mapGetters('app', [
      'getMerchantId',
    ]),
    ...mapGetters('stakeholdersDetails', [
      'getControlPerson',
      'isControlPersonValid',
      'getBeneficiaries',
      'getAdditionalBeneficiaries',
      'canAddMoreBeneficiaries',
      'canAddAdditionalBeneficiary',
      'getOwnershipErrors',
      'getErrors',
    ]),
    ...mapState({
      ownershipError: state => state.stakeholdersDetails.errors.ownership,
    }),
  },
  mounted() {
    if (this.isUpdate) {
      this.openModel = true;
    }
  },
  methods: {
    proceed() {
      if (!this.isControlPersonValid) {
        this.missingControlPersonError = true;    
      } else {
        this.missingControlPersonError = false;

        // Emit FullStory event
        FullStoryHelper.emitIncrementalOnboardingEvent(OWNERSHIP_OVERVIEW_PROCEED,
          UPDATE_OWNERSHIP_OVERVIEW_PROCEED, this.isUpdate);

        this.$emit('proceed');
      }
    },
    BeneficialOwnerTypes() {
      return BeneficialOwnerTypes
    },
    ...mapActions('stakeholdersDetails', [
      'setOwnerType',
      'createStakeholder',
      'deleteStakeholder',
      'resetControlPerson',
      'updateStakeholder',
    ]),
    ...mapActions('merchantPayfacDetails', ['setValuesFromIncrementalOnboardingResponse']),
    openBeneficiaryPopup: function () {
      this.activeBeneficiaryIndex = 0;
      this.openModel = true;
      this.editMode = false;
      this.missingControlPersonError = false;
    },
    createBeneficiaryWithOwnerType: function (ownershipType) {
      this.createAndOpenBeneficiaryPopup(() => {
        this.setOwnerType({i: this.activeBeneficiaryIndex, value: ownershipType})
      })
    },
    createAndOpenBeneficiaryPopup: function (callback) {
      this.createStakeholder().then((stakeholderIndex) => {
        this.activeBeneficiaryIndex = stakeholderIndex;
        this.openModel = true;
        this.editMode = false;
        if (callback) {
          callback();
        }
      })
    },
    editBeneficiary: function (index) {
      this.activeBeneficiaryIndex = index;
      this.editMode = true;
      this.openModel = true;
      this.beneficiaryBackup = JSON.parse(JSON.stringify(this.getBeneficiaries[index]));
    },
    onDismissFromAddBeneficiary: function () {
      if (this.editMode) {
        if (this.beneficiaryBackup) {
          this.updateStakeholder({i: this.activeBeneficiaryIndex, beneficiary: this.beneficiaryBackup})
          this.beneficiaryBackup = null;
        }
        return;
      }
      if (this.activeBeneficiaryIndex !== 0) {
        this.deleteStakeholder(this.activeBeneficiaryIndex)
      } else {
        this.resetControlPerson();
      }
    },
    deleteBeneficiary: async function (index) {
      try {
        const draftId = this.getBeneficiaries[index][StakeholderField.DRAFT_ID].value;
        let response = await merchantOnboardingApi.deleteBeneficiary(
            this.getMerchantId,
            draftId,
            {
              requestType: OnboardingRequest.RequestTypeEnum.DELETEBENEFICIARY,
              draft_id: draftId,
            },
        );
        await this.setValuesFromIncrementalOnboardingResponse(response);
        this.deleteStakeholder(index);
      } catch (error) {
        this.handleError(error);
      }
    },
    handleError(error) {
      if (error.source?.sub_errors?.length) {
        this.$danger('Could not delete beneficiary!');
        error.source?.sub_errors?.forEach(subError => {
          this.setErrors({key: subError.field, errors: subError.message});
        });
      } else {
        this.$danger(error.source?.title);
      }
    },
  },
}
</script>

<style lang="scss">
.incremental-onboarding-view-container {
   .s-overflow-menu-items {
     li {
      padding: 20px !important;
    }
  }
}
</style>

<style src="../../../node_modules/v-calendar/lib/v-calendar.min.css"/>
